import { ElementType, lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
// hooks
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import AuthGuard from '../careflow/ph-core/authentication/guard/AuthGuard';
import GuestGuard from '../careflow/ph-core/authentication/guard/GuestGuard';
// config
import { PATH_AFTER_LOGIN, REDIRECT_AUTHORIZE_ROUTE } from '../config';
// components
import Debug from 'src/sections/debug';
import CarePlanAutomationIndex from 'src/ccm/settings/care-plan-automation';
import LoadingScreen from '../components/LoadingScreen';

//crs components
import OrganizationCreate from 'src/careflow/ph-core/admin/organization/pages/OrganizationCreate';
import OrganizationList from 'src/careflow/ph-core/admin/organization/pages/OrganizationList';
import CaseDetails from 'src/careflow/care-coordination/case/detail/CaseDetails';
import ReferralDetails from 'src/careflow/care-coordination/referral/components/ReferralDetails';
import ReferralList from 'src/careflow/care-coordination/referral/ReferralList';
import TagsList from 'src/careflow/care-coordination/tags/TagsList';
import ActivityListAutomationIndex from 'src/ccm/settings/activity-list-automation';
import AssessmentsSettingsIndex from 'src/ccm/settings/assessments';
import ClientsSettings from 'src/ccm/settings/clients';
import { CRS_PATH, PATH_DASHBOARD } from './paths';
import SelectOrgNRole from 'src/careflow/ph-core/authentication/component/SelectOrgNRole';
import ChildReferralDetails from 'src/careflow/care-coordination/inbound-referral/ChildReferralDetails';
import PatientEngagementDetails from 'src/careflow/engagement-hub/patient-engagement/patient/PatientEngagementDetails';
import CensusSettings from 'src/ccm/settings/census/CensusSettings';
import ProgramSettingsIndex from 'src/ccm/settings/program-settings';
import { ThemeSettingList } from 'src/ccm/settings/theme-settings/ThemeSettingList';
import UsersOrganizationSettings from 'src/ccm/settings/users-organization-settings/UsersOrganizationSettings';
import Example from 'src/careflow/YoCare/Example';
import PersonDetails from 'src/careflow/YoCare/Persons/PersonsDetails/PersonsDetails';
import PersonsList from 'src/careflow/YoCare/Persons/PersonsList/PersonsList';
import UsersDetails from 'src/careflow/YoCare/Users/UsersDetails';
import UsersList from 'src/careflow/YoCare/Users/UsersList/UsersList';
import TaskListContainer from '../careflow/care-coordination/task/main/TaskListContainer';
import PatientListContainer from 'src/careflow/care-coordination/patient/list/PatientListContainer';
import PatientEngagementListContainer from '../careflow/engagement-hub/patient-engagement/patient/PatientEngagementListContainer';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import IncidentDetails from 'src/careflow/data-quality/incident/components/IncidentDetails';
import IncidentList from 'src/careflow/data-quality/incident/IncidentList';
import CaseListContainer from 'src/careflow/care-coordination/case/list/CaseListContainer';
import { OrgValidationType } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import { getCareflowToken } from 'src/careflow/ph-core/authentication/util/token';
import TaskTab from 'src/careflow/inck/nat-task/TaskTab';
import SelectTenant from 'src/careflow/ph-core/multi-tenant/tenant/component/SelectTenant';
import { AclBasedGuard } from 'src/careflow/ph-core/admin/permissions/guards/AclBasedGuard';
import { OrganizationBasedGuard } from 'src/careflow/ph-core/admin/permissions/guards/OrganizationBasedGuard';
import { DbGuardAllowedResources } from 'src/careflow/ph-core/admin/permissions/guards/organizationBasedGuard.utils';
import WorkflowList from 'src/careflow/ph-core/workflow-management/WorkflowList';
import WorkflowDetails from 'src/careflow/ph-core/workflow-management/WorkflowDetails';
import ChecklistDetails from 'src/careflow/ph-core/workflow-management/ChecklistDetails';
import ChecklistList from 'src/careflow/ph-core/workflow-management/ChecklistList';
import CatalogList from 'src/careflow/ph-core/workflow-management/CatalogList';
import ActivityDefinitionList from 'src/careflow/care-coordination/management/ActivityDefinitionList';
import PlanDefinitionList from 'src/careflow/care-coordination/management/PlanDefinitionList';
import RegistryList from 'src/careflow/ph-core/workflow-management/RegistryList';
import NewPatient from 'src/careflow/care-coordination/patient/detail/components/NewPatient';
import PatientDetails from 'src/careflow/care-coordination/patient/detail/PatientDetails';
import FhirOrganizationList from 'src/careflow/ph-core/fhir-organization/pages/FhirOrganizationList';
import FhirOrganizationDetails from 'src/careflow/ph-core/fhir-organization/pages/FhirOrganizationDetails';
import CustomFieldsList from 'src/sections/careflow/custom-fields/CustomFieldsList';
import { EpisodeOfCareList } from 'src/careflow/care-coordination/episode-of-care';
import EpisodeOfCareDetails from 'src/careflow/care-coordination/episode-of-care/pages/EpisodeOfCareDetails';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

function RootRedirect({ baseFrontUrl, backendBaseUrl }: any) {
  const { isAuthenticated } = useAuth();
  const { configurations, finishedLoading } = useTenantConfigData();

  const careflowToken = getCareflowToken(true);

  if (!careflowToken || !isAuthenticated) {
    window.location.replace(backendBaseUrl + REDIRECT_AUTHORIZE_ROUTE);

    return null;
  }

  if (!finishedLoading) {
    return <LoadingScreen />;
  }

  window.location.replace(baseFrontUrl + configurations.dashboardRoute);

  return null;
}

export function getRouter(baseFrontUrl: string, backendBaseUrl: string) {
  return createBrowserRouter([
    {
      path: 'templates/:keyRegistry',
      element: <LandingPage />,
    },
    {
      path: '/',
      element: <RootRedirect baseFrontUrl={baseFrontUrl} backendBaseUrl={backendBaseUrl} />,
    },
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'logout',
          element: (
            <GuestGuard>
              <div>logout</div>
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: <AclBasedGuard children={<DashboardLayout />} acls={[crsAcls.CRS.DASHBOARD.VIEW]} />,
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        { path: 'time-entries', element: <TimeEntries /> },
        { path: 'timesheets', element: <Timesheets /> },
        { path: 'time-tracking-administration', element: <TimeTrackingAdministration /> },
        {
          path: 'app/*',
          element: <AclBasedGuard children={<GeneralApp />} acls={[crsAcls.CRS.DASHBOARD.VIEW]} />,
        },
        {
          path: 'organization',
          children: [
            {
              path: 'list',
              element: (
                <AclBasedGuard
                  children={<OrganizationList />}
                  acls={[crsAcls.ADMIN.ORGANIZATIONS.MENU]}
                />
              ),
            },
            {
              path: 'new',
              element: (
                <AclBasedGuard
                  children={<OrganizationCreate />}
                  acls={[crsAcls.ADMIN.ORGANIZATIONS.ADD]}
                />
              ),
            },
            {
              path: ':uuid/edit',
              element: (
                <AclBasedGuard
                  children={<OrganizationCreate />}
                  acls={[crsAcls.ADMIN.ORGANIZATIONS.EDIT]}
                />
              ),
            },
          ],
        },
        {
          path: 'fhir-organization',
          children: [
            {
              path: 'list',
              element: (
                <AclBasedGuard
                  children={<FhirOrganizationList />}
                  acls={[crsAcls.ADMIN.FHIR_ORGANIZATIONS.MENU]}
                />
              ),
            },
            {
              path: ':uuid',
              element: (
                <AclBasedGuard
                  children={<FhirOrganizationDetails />}
                  acls={[crsAcls.ADMIN.ORGANIZATIONS.VIEW]}
                />
              ),
            },
          ],
        },
        {
          path: 'settings',
          children: [{ path: 'list', element: <ThemeSettingList /> }],
        },
        {
          path: 'referral',
          element: <AclBasedGuard children={<ReferralList />} acls={[crsAcls.CRS.REFERRAL.MENU]} />,
        },
        {
          path: 'registry',
          element: <RegistryList />,
        },
        {
          path: 'catalog',
          element: <CatalogList />,
        },
        {
          path: 'activity-definition',
          element: (
            <AclBasedGuard
              children={<ActivityDefinitionList />}
              acls={[crsAcls.CRS.ACTIVITY_DEFINITION.MENU]}
            />
          ),
        },
        {
          path: 'plan-definition',
          element: (
            <AclBasedGuard
              children={<PlanDefinitionList />}
              acls={[crsAcls.CRS.PLAN_DEFINITION.MENU]}
            />
          ),
        },
        {
          path: 'workflow',
          children: [
            {
              element: (
                <AclBasedGuard children={<WorkflowList />} acls={[crsAcls.ADMIN.WORKFLOW.MENU]} />
              ),
              index: true,
            },
            {
              path: ':code',
              element: (
                <AclBasedGuard
                  children={<WorkflowDetails />}
                  acls={[crsAcls.ADMIN.WORKFLOW.MENU]}
                />
              ),
            },
          ],
        },
        {
          path: 'checklist',
          children: [
            {
              element: (
                <AclBasedGuard children={<ChecklistList />} acls={[crsAcls.ADMIN.CHECKLIST.MENU]} />
              ),
              index: true,
            },
            {
              path: ':code',
              element: (
                <AclBasedGuard
                  children={<ChecklistDetails />}
                  acls={[crsAcls.ADMIN.CHECKLIST.MENU]}
                />
              ),
            },
          ],
        },
        {
          path: 'task',
          element: (
            <AclBasedGuard children={<TaskListContainer />} acls={[crsAcls.CRS.TASK.MENU]} />
          ),
        },
        {
          path: 'tags',
          element: <TagsList />,
        },
        {
          path: 'episode-of-care',
          children: [
            {
              path: 'list',
              element: (
                <AclBasedGuard
                  children={<EpisodeOfCareList />}
                  acls={[crsAcls.CRS.EPISODE_OF_CARE.MENU]}
                />
              ),
            },
            {
              path: ':uuid',
              element: (
                <AclBasedGuard
                  children={<EpisodeOfCareDetails />}
                  acls={[crsAcls.CRS.EPISODE_OF_CARE.VIEW]}
                />
              ),
            },
          ],
        },
        {
          path: 'permissions',
          element: (
            <AclBasedGuard children={<Permissions />} acls={[crsAcls.ADMIN.PERMISSIONS.MENU]} />
          ),
        },
        {
          path: 'custom-fields',
          element: (
            <AclBasedGuard
              children={<CustomFieldsList />}
              acls={[crsAcls.ADMIN.CUSTOM_FIELDS.MENU]}
            />
          ),
        },
        {
          path: 'case',
          element: (
            <AclBasedGuard children={<CaseListContainer />} acls={[crsAcls.CRS.CASE.MENU]} />
          ),
        },
        {
          path: 'yocare/*',
          children: [
            {
              path: 'users',
              element: (
                <AclBasedGuard children={<UsersList />} acls={[crsAcls.YOCARE.USERS.MENU]} />
              ),
            },
            {
              path: 'persons',
              element: (
                <AclBasedGuard children={<PersonsList />} acls={[crsAcls.YOCARE.PERSONS.MENU]} />
              ),
            },
            { path: 'dataSystems', element: <Example /> },
            {
              path: 'userDetails/:id',
              element: (
                <AclBasedGuard children={<UsersDetails />} acls={[crsAcls.YOCARE.USERS.ALL]} />
              ),
            },
            {
              path: 'personDetails/:id',
              element: (
                <AclBasedGuard children={<PersonDetails />} acls={[crsAcls.YOCARE.PERSONS.ALL]} />
              ),
            },
          ],
        },
        {
          path: PATH_DASHBOARD.crs.patient.list.value,
          handle: 'Patients',
          children: [
            {
              index: true,
              element: (
                <AclBasedGuard
                  children={<PatientListContainer />}
                  acls={[crsAcls.CRS.PATIENT.VIEW]}
                />
              ),
            },
            {
              path: PATH_DASHBOARD.crs.patient.details.value,
              element: (
                <OrganizationBasedGuard
                  children={
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.GENERAL.VIEW]}
                    />
                  }
                  resourcePaths={[
                    {
                      resourceName: 'Patient',
                      resourcePath: 'managingOrganization.reference',
                      resourceType: OrgValidationType.FHIR,
                    },
                  ]}
                  identifierNameInPath={PATH_DASHBOARD.crs.patient.details.getIdField()}
                />
              ),
              handle: 'Patient Details',
              children: [
                {
                  path: 'general',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.GENERAL.VIEW]}
                    />
                  ),
                },
                {
                  path: 'groups',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.GROUPS.VIEW]}
                    />
                  ),
                },
                {
                  path: 'referrals-cases',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.REFERRAL_CASES.VIEW]}
                    />
                  ),
                },
                {
                  path: 'health',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.HEALTH.VIEW]}
                    />
                  ),
                },
                {
                  path: 'insurance',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.INSURANCE.VIEW]}
                    />
                  ),
                },
                {
                  path: 'documents',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.DOCUMENTS.VIEW]}
                    />
                  ),
                },
                {
                  path: 'tasks',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.TASKS.VIEW]}
                    />
                  ),
                },
                {
                  path: 'engagement-hub',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.ENGAGEMENT_HUB.VIEW]}
                    />
                  ),
                },
                {
                  path: 'consents',
                  element: (
                    <AclBasedGuard
                      children={<PatientDetails />}
                      acls={[crsAcls.CRS.PATIENT.TABS.CONSENTS.VIEW]}
                    />
                  ),
                },
              ],
            },
            {
              path: PATH_DASHBOARD.crs.patient.new.value,
              element: <AclBasedGuard children={<NewPatient />} acls={[crsAcls.CRS.PATIENT.ADD]} />,
              handle: 'New Patient',
            },
          ],
        },
        {
          path: 'nat/',
          children: [{ path: 'task/:id', element: <TaskTab /> }],
        },
        {
          path: 'engagementhub/',
          children: [
            {
              path: 'patientengagement',
              element: (
                <AclBasedGuard
                  children={<PatientEngagementListContainer />}
                  acls={[crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.VIEW]}
                />
              ),
            },
            {
              path: 'patientengagement/:id',
              element: (
                <OrganizationBasedGuard
                  children={
                    <AclBasedGuard
                      children={<PatientEngagementDetails />}
                      acls={[crsAcls.ENGAGEMENT_HUB.PATIENT_ENGAGEMENT.VIEW]}
                    />
                  }
                  resourcePaths={[
                    {
                      resourceName: DbGuardAllowedResources.PatientEngagement,
                      resourcePath: 'patientManagingOrgUUID',
                      resourceType: OrgValidationType.DB,
                    },
                  ]}
                  identifierNameInPath={'id'}
                />
              ),
            },
          ],
        },
        {
          path: 'referral',
          children: [
            {
              path: ':id',
              element: (
                <AclBasedGuard children={<ReferralDetails />} acls={[crsAcls.CRS.REFERRAL.EDIT]} />
              ),
            },
            {
              path: ':name/edit',
              element: (
                <AclBasedGuard children={<ReferralDetails />} acls={[crsAcls.CRS.REFERRAL.EDIT]} />
              ),
            },
          ],
        },
        {
          path: 'child-referral',
          children: [
            {
              path: ':id',
              element: (
                <AclBasedGuard
                  children={<ChildReferralDetails />}
                  acls={[crsAcls.CRS.REFERRAL.CHILD_REFERRAL.EDIT]}
                />
              ),
            },
          ],
        },
        {
          path: 'case',
          children: [
            {
              path: ':id',
              element: (
                <OrganizationBasedGuard
                  children={
                    <AclBasedGuard children={<CaseDetails />} acls={[crsAcls.CRS.CASE.VIEW]} />
                  }
                  resourcePaths={[
                    {
                      resourceName: 'CarePlan',
                      resourcePath: 'subject.reference',
                      resourceType: OrgValidationType.FHIR,
                    },
                    {
                      resourceName: 'Patient',
                      resourcePath: 'managingOrganization.reference',
                      resourceType: OrgValidationType.FHIR,
                    },
                  ]}
                  identifierNameInPath={'id'}
                />
              ),
            },
          ],
        },
        {
          path: 'dataquality/',
          children: [
            {
              path: 'incident',
              element: (
                <AclBasedGuard
                  children={<IncidentList />}
                  acls={[crsAcls.DATA_QUALITY.INCIDENT.MENU]}
                />
              ),
            },
            {
              path: 'incident/:id',
              element: (
                <AclBasedGuard
                  children={<IncidentDetails />}
                  acls={[crsAcls.DATA_QUALITY.INCIDENT.EDIT]}
                />
              ),
            },
          ],
        },

        {
          path: 'user',
          children: [
            { element: <Navigate to="/dashboard/user/profile" replace />, index: true },
            {
              path: 'profile',
              element: (
                <AclBasedGuard children={<UserProfile />} acls={[crsAcls.ADMIN.USERS.PROFILE]} />
              ),
            },
            {
              path: 'cards',
              element: (
                <AclBasedGuard children={<UserCards />} acls={[crsAcls.ADMIN.USERS.CARDS]} />
              ),
            },
            {
              path: 'list',
              element: <AclBasedGuard children={<UserList />} acls={[crsAcls.ADMIN.USERS.MENU]} />,
            },
            {
              path: 'new',
              element: <AclBasedGuard children={<UserCreate />} acls={[crsAcls.ADMIN.USERS.ADD]} />,
            },
            {
              path: ':uuid/edit',
              element: (
                <AclBasedGuard children={<UserCreate />} acls={[crsAcls.ADMIN.USERS.EDIT]} />
              ),
            },
            {
              path: 'account',
              element: (
                <AclBasedGuard children={<UserAccount />} acls={[crsAcls.ADMIN.USERS.ACCOUNT]} />
              ),
            },
          ],
        },
        { path: 'permission-denied', element: <PermissionDenied /> },
        {
          path: 'templates/:keyRegistry',
          element: <LandingPage />,
        },
      ],
    },
    {
      path: CRS_PATH.provider.root,
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={CRS_PATH.provider.list} replace />, index: true },
        {
          path: CRS_PATH.provider.list,
          element: (
            <AclBasedGuard children={<PractitionerList />} acls={[crsAcls.CRS.PRACTITIONER.MENU]} />
          ),
        },
        {
          path: ':practitionerId',
          element: (
            <AclBasedGuard
              children={<PractitionerDetails />}
              acls={[crsAcls.CRS.PRACTITIONER.VIEW]}
            />
          ),
        },
      ],
    },
    {
      path: 'settings',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: 'care-plan-automation/*',
          element: <CarePlanAutomationIndex />,
        },
        {
          path: 'activity-list-automation/*',
          element: <ActivityListAutomationIndex />,
        },
        {
          path: 'care-member',
          element: <ComingSoon />,
        },
        {
          path: 'assessments/*',
          element: <AssessmentsSettingsIndex />,
        },
        {
          path: 'clients/*',
          element: <ClientsSettings />,
        },
        {
          path: 'programs/*',
          element: <ProgramSettingsIndex />,
        },
        {
          path: 'care-member/assign',
          element: <ComingSoon />,
        },
        {
          path: 'script',
          element: <ComingSoon />,
        },
        {
          path: 'provider',
          element: <ComingSoon />,
        },
        {
          path: 'template',
          element: <ComingSoon />,
        },
        {
          path: 'consent-forms',
          element: <ComingSoon />,
        },
        {
          path: 'census',
          element: <CensusSettings />,
        },
        {
          path: 'users-organization',
          element: <UsersOrganizationSettings />,
        },
      ],
    },

    // Debug
    {
      path: '_debug/*',
      element: <Debug />,
    },
    // Misc
    {
      path: 'survey-saved',
      element: <SurveySaved />,
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: '*', element: <Navigate to="/404" replace /> },
        {
          path: 'select-organization-n-role',
          element: (
            <AuthGuard>
              <SelectOrgNRole />
            </AuthGuard>
          ),
        },
        { path: 'select-tenant', element: <SelectTenant /> },
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: 'pricing', element: <Pricing /> },
        { path: 'payment', element: <Payment /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: 'single-logout', element: <SingleLogout /> },
        { path: 'sso-logout', element: <SSOLogout /> },
        { path: 'inactive-user', element: <InactiveUser /> },
        { path: 'session-expired', element: <SessionExpired /> },
      ],
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD

// GENERAL
const GeneralApp = Loadable(lazy(() => import('../pages/dashboard/GeneralApp')));

//ADMIN
const Permissions = Loadable(
  lazy(() => import('../careflow/ph-core/admin/permissions/Permissions'))
);

// USER
const UserProfile = Loadable(lazy(() => import('../careflow/ph-core/admin/user/UserProfile')));
const UserCards = Loadable(lazy(() => import('../careflow/ph-core/admin/user/UserCards')));
const UserList = Loadable(lazy(() => import('../careflow/ph-core/admin/user/UserList')));
const UserAccount = Loadable(lazy(() => import('../careflow/ph-core/admin/user/UserAccount')));
const UserCreate = Loadable(lazy(() => import('../careflow/ph-core/admin/user/UserCreate')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const SingleLogout = Loadable(lazy(() => import('../pages/SingleLogout')));
const SSOLogout = Loadable(lazy(() => import('../pages/SSOLogout')));
const SessionExpired = Loadable(lazy(() => import('../pages/SessionExpired')));
const InactiveUser = Loadable(lazy(() => import('../pages/InactiveUser')));
const Pricing = Loadable(lazy(() => import('../pages/Pricing')));
const Payment = Loadable(lazy(() => import('../pages/Payment')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
const LandingPage = Loadable(lazy(() => import('../pages/LandingPage')));
const SurveySaved = Loadable(lazy(() => import('../sections/misc/SurveySaved')));
const PractitionerList = Loadable(
  lazy(
    () =>
      import(
        '../careflow/care-coordination/practitioner/provider/PractitionerList/PractitionerList'
      )
  )
);
const PractitionerDetails = Loadable(
  lazy(
    () =>
      import(
        '../careflow/care-coordination/practitioner/provider/PractitionerDetails/PractitionerDetails'
      )
  )
);

const TimeEntries = Loadable(lazy(() => import('../pages/dashboard/TimeEntries')));
const Timesheets = Loadable(lazy(() => import('../pages/dashboard/Timesheets')));
const TimeTrackingAdministration = Loadable(
  lazy(() => import('../pages/dashboard/TimeTrackingAdministration'))
);
